import type { RunVisualViewId } from "@/api/run-visuals";
import type { RunVisualAvailability, RunVisualView } from "@/components/runs/run-visual-views";

/**
 * The run workspace's MAIN-AREA MODE, and the side panel that pairs with it
 * (flowstack#991).
 *
 * The workspace used to be a fixed split: the DAG on the left, always, with a
 * 26rem panel on the right carrying whatever visual the run had produced. That
 * shape gave the graph the whole main area and left every *visual* surface in
 * a column too narrow to read — three ~90px torsion cards, or a 3D structure
 * in a box smaller than the tile-settings list beneath it.
 *
 * The operator's model (#991, second comment) replaces it: **the main area is
 * a mode, and the side panel follows the mode.** The user picks what the main
 * area shows; whatever that is gets the full width the DAG used to monopolise,
 * and the side panel is then always the DETAIL of that thing rather than a
 * generic dumping ground.
 *
 * **VIS/V6 makes the rail a STACK.** #991's table paired each mode with ONE
 * panel, which meant tile settings and the molecule could never be on screen
 * together: selecting a step in the graph replaced the structure, and getting
 * it back meant leaving the graph. The pairing survives — what changes is that
 * the rail is a list, and a selected step appends to it instead of taking it
 * over.
 *
 * | Main area     | Rail, top to bottom                              |
 * |---------------|--------------------------------------------------|
 * | DAG           | the molecule · tile settings                     |
 * | Torsion plots | the molecule · the selected fit's numbers        |
 * | Molecule      | the fragment list and its fit numbers            |
 *
 * ...plus **tile settings appended to any of them while a step is selected**.
 * The last two rows are the same pairing inverted: structure beside numbers,
 * with whichever one you came to read taking the main area.
 *
 * Kept as pure functions over the visuals index (like `run-visual-views.ts`,
 * whose availability rules this reuses rather than restates) so the mode
 * table is testable without mounting reactflow, a chart or a 3D viewer.
 */

/** The main area's mode. `dag` plus every view a run can visualize. */
export type RunWorkspaceModeId = "dag" | RunVisualViewId;

/**
 * One SECTION of the rail beside the main area.
 *
 * Plural since VIS/V6: the rail carries an ordered list of these rather than
 * exactly one ({@link sidePanelsFor}).
 */
export type RunWorkspaceSideId = "tile" | "molecule" | "fragments";

export interface RunWorkspaceMode {
  id: RunWorkspaceModeId;
  label: string;
  availability: RunVisualAvailability;
  /** The visual view backing this mode; `null` for the DAG. */
  view: RunVisualView | null;
}

/**
 * DAG first, then the visual views in their existing order.
 *
 * Spliced onto `RUN_VISUAL_VIEW_ORDER` rather than re-listed, so the visual
 * modes cannot drift out of step with the order the rest of the app uses.
 */
const DAG_LABEL = "DAG";

/**
 * Build the mode list.
 *
 * The DAG is unconditionally `ready`: this workspace only renders for a run
 * that HAS a flow graph (`RunWorkspace` bails to an empty state otherwise), so
 * there is no state in which the graph is pending or absent. Every other mode
 * inherits its availability from `deriveRunVisualViews` — the same three-state
 * ready/pending/absent rule the old selector used, unchanged: a mode whose data
 * has not arrived yet is offered-but-disabled and says *pending*, and once the
 * run is terminal it says the run produced none. Neither is ever silently
 * hidden, because a hidden mode reads as "this portal cannot do that".
 */
export function deriveWorkspaceModes(views: readonly RunVisualView[]): RunWorkspaceMode[] {
  return [
    { id: "dag" as const, label: DAG_LABEL, availability: "ready" as const, view: null },
    ...views.map((view) => ({
      id: view.id,
      label: view.label,
      availability: view.availability,
      view,
    })),
  ];
}

/**
 * The mode to show, given the user's pick.
 *
 * Falls back to the DAG rather than to "the first ready mode": the DAG is the
 * only mode guaranteed to have something to draw, and it is what the tab
 * showed before a user picked anything. A run whose torsion plots were
 * superseded by a resume therefore lands back on the graph, never on a mode
 * whose data has gone.
 */
export function resolveWorkspaceMode(
  modes: readonly RunWorkspaceMode[],
  picked: RunWorkspaceModeId | null
): RunWorkspaceMode {
  const chosen = modes.find((m) => m.id === picked && m.availability === "ready");
  // By ID, not `modes[0]` (Rupert PR-999 r1): the fallback IS "the DAG", and
  // spelling it as a position makes that true only for as long as
  // `deriveWorkspaceModes` happens to emit the DAG first. A reorder — or a
  // caller assembling its own `modes` array — would silently turn the
  // guarantee into "whatever is first" with nothing to catch it.
  return chosen ?? modes.find((m) => m.id === "dag") ?? modes[0];
}

/**
 * The sections the rail carries for a mode — the whole of #991's "side panel
 * dependent", **now a STACK rather than a single slot (VIS/V6)**.
 *
 * Exhaustive over `RunWorkspaceModeId` by construction (a `Record`, not a
 * switch with a default), so adding a mode without deciding its rail is a type
 * error rather than a silent fallback to the tile settings.
 *
 * The order is the artboard's (`dev/design/flowstack-workspace/Main.dc.html`,
 * in the project-manager tree): the structure sits at the TOP of the rail and
 * the numbers about it come underneath. That ordering is the reason a
 * selection appends rather than prepends — see {@link sidePanelsFor}.
 */
const SIDE_BY_MODE: Record<RunWorkspaceModeId, readonly RunWorkspaceSideId[]> = {
  // The graph pairs with the tile settings as it always did — and now ALSO
  // keeps the structure, which is the whole of VIS/V6's complaint: clicking a
  // step used to cost you the molecule.
  dag: ["molecule", "tile"],
  // Reading a fit: the numbers are the main event, the structure is the
  // reference beside them. Split into two sections rather than one panel
  // holding both, so each is its own container with its own maximise.
  torsion: ["molecule", "fragments"],
  // ...and inverted. Reading a structure, the fit numbers are the reference —
  // and the structure is not repeated in the rail, because it IS the main area.
  molecule: ["fragments"],
};

/**
 * The rail's sections, top to bottom.
 *
 * **A selected step ADDS a section; it never replaces the rail.** That is the
 * unit's requirement stated as code: the rail used to be
 * `sidePanelFor(mode.id)` — one id, derived from the mode alone — so tile
 * settings and the molecule were mutually exclusive *by construction* and the
 * only way to see the other was to switch modes, which is the behaviour the
 * operator reported as "we are still very far from user-friendly".
 *
 * `tile` is appended rather than prepended so the rail's top does not move
 * under the user: the structure is the anchor, and the step's settings arrive
 * beneath it where a new section is expected to appear.
 *
 * The DAG carries `tile` unconditionally (it is in its `SIDE_BY_MODE` row)
 * because an empty tile panel there says *no node selected*, which is an
 * affordance — it names the thing clicking a node fills. In a visual mode
 * there is no graph on screen to click, so an empty tile section would be a
 * slot with no way to fill it.
 *
 * **`molecule` is gated on AVAILABILITY, not on the mode alone
 * (flowstack#1221 finding 1).** VIS/V6 put the structure in the DAG's rail
 * from the mode table only, so a run that never carried a structure-bearing
 * artifact got a *Molecule* panel saying "this run produced none" — a section
 * whose entire content is the news that it has no content. That is not the
 * same as the empty tile panel above: the tile panel names an action (click a
 * node), while a structure section on a run that has no structure names
 * nothing the user can do.
 *
 * The gate is `absent` ONLY. A `pending` molecule keeps its section, because
 * `pending` means the run may still produce one and the three-state rule
 * (`run-visual-views.ts`) is explicit that "not yet" must be offered and said
 * out loud rather than hidden — a section that appears mid-run is expected,
 * where a section that appears on a settled run would be a bug.
 *
 * The availability passed here must be the RUN's, not the selected step's —
 * see `runMoleculeAvailability` in `run-visual-views.ts`; a step with no
 * structure input must not make the section disappear.
 */
export function sidePanelsFor(
  mode: RunWorkspaceModeId,
  opts: { hasSelection: boolean; moleculeAvailability: RunVisualAvailability }
): RunWorkspaceSideId[] {
  const base = SIDE_BY_MODE[mode].filter(
    (side) => side !== "molecule" || opts.moleculeAvailability !== "absent"
  );
  return opts.hasSelection && !base.includes("tile") ? [...base, "tile"] : [...base];
}

/** Heading for the side panel — says what it is showing, never "detail". */
const SIDE_HEADINGS: Record<RunWorkspaceSideId, string> = {
  tile: "Tile settings",
  molecule: "Molecule",
  fragments: "Torsion fits",
};

export function sideHeadingFor(side: RunWorkspaceSideId): string {
  return SIDE_HEADINGS[side];
}
